import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2 } from 'lucide-react';
import { DASH, authHeader } from '../api';
import { SectionCard, Spinner, PrimaryButton, GhostButton, DangerButton, fmtDate } from '../ui';
import ConfirmDialog from '../../components/ConfirmDialog';

const ScheduleSection = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${DASH}/schedule`, { headers: authHeader() })
      .then((res) => { setData(res.data); setDraft(res.data); })
      .catch(() => setError('Could not load schedule.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = data && draft && (
    data.open_time !== draft.open_time || data.close_time !== draft.close_time || data.buffer_minutes !== draft.buffer_minutes
  );

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.patch(`${DASH}/schedule`, {
        open_time: draft.open_time,
        close_time: draft.close_time,
        buffer_minutes: parseInt(draft.buffer_minutes, 10),
      }, { headers: authHeader() });
      setData(res.data);
      setDraft(res.data);
      setSuccess('Business hours updated.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save schedule.');
    } finally {
      setSaving(false);
    }
  };

  const addBlockedDay = async () => {
    if (!newDate) return;
    try {
      await axios.post(`${DASH}/schedule/blocked-days`, { date: newDate, reason: newReason }, { headers: authHeader() });
      setNewDate(''); setNewReason('');
      load();
    } catch {
      setError('Could not add closure day.');
    }
  };

  const removeBlockedDay = async () => {
    if (!removeTarget) return;
    try {
      await axios.delete(`${DASH}/schedule/blocked-days/${removeTarget}`, { headers: authHeader() });
      setRemoveTarget(null);
      load();
    } catch {
      setError('Could not remove closure day.');
    }
  };

  if (loading || !draft) return <Spinner label="Loading schedule…" />;

  return (
    <div data-testid="wellness-admin-section-schedule" className="space-y-6">
      <div>
        <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>
          Schedule Control
        </h2>
        <p className="text-sm text-[#5a7062] mt-1">Business hours, buffer time, and full-day closures.</p>
      </div>
      {error && <div data-testid="schedule-error" className="text-sm text-red-700">{error}</div>}
      {success && <div data-testid="schedule-success" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{success}</div>}

      <SectionCard
        title="Daily Hours"
        subtitle={`60-min sessions, ${draft.buffer_minutes}-min buffer between slots.`}
        testId="schedule-hours-card"
        actions={
          <PrimaryButton testId="schedule-save-button" disabled={!dirty || saving} onClick={save}>
            <Save size={15} /> Save Hours
          </PrimaryButton>
        }
      >
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5a7062] mb-1">Opens</label>
            <input data-testid="schedule-open-time" type="time" value={draft.open_time} onChange={(e) => setDraft({ ...draft, open_time: e.target.value })} className="w-full h-11 px-3 border border-[#d8cfb4] rounded-lg font-semibold text-[#0d1f15] focus:outline-none focus:border-[#d97736] focus:ring-2 focus:ring-[#d97736]/20" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5a7062] mb-1">Closes</label>
            <input data-testid="schedule-close-time" type="time" value={draft.close_time} onChange={(e) => setDraft({ ...draft, close_time: e.target.value })} className="w-full h-11 px-3 border border-[#d8cfb4] rounded-lg font-semibold text-[#0d1f15] focus:outline-none focus:border-[#d97736] focus:ring-2 focus:ring-[#d97736]/20" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5a7062] mb-1">Buffer (min)</label>
            <input data-testid="schedule-buffer-minutes" type="number" min="0" max="120" value={draft.buffer_minutes} onChange={(e) => setDraft({ ...draft, buffer_minutes: e.target.value })} className="w-full h-11 px-3 border border-[#d8cfb4] rounded-lg font-semibold text-[#0d1f15] focus:outline-none focus:border-[#d97736] focus:ring-2 focus:ring-[#d97736]/20" />
          </div>
        </div>
        <p className="text-xs text-[#5a7062] mt-4">
          Preview of today's slots: {draft.slot_preview?.join(', ') || '—'}
        </p>
      </SectionCard>

      <SectionCard title="Full-Day Closures" subtitle="Holidays, vacations, or one-off closures." testId="schedule-blocked-days-card">
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5a7062] mb-1">Date</label>
            <input data-testid="schedule-new-blocked-date" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-11 px-3 border border-[#d8cfb4] rounded-lg" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-[#5a7062] mb-1">Reason</label>
            <input data-testid="schedule-new-blocked-reason" type="text" placeholder="e.g. Thanksgiving" value={newReason} onChange={(e) => setNewReason(e.target.value)} className="w-full h-11 px-3 border border-[#d8cfb4] rounded-lg" />
          </div>
          <GhostButton testId="schedule-add-blocked-day" onClick={addBlockedDay} disabled={!newDate}>
            <Plus size={14} /> Add Closure
          </GhostButton>
        </div>
        {(!draft.blocked_days || draft.blocked_days.length === 0) ? (
          <p data-testid="schedule-blocked-days-empty" className="text-sm text-[#5a7062]">No upcoming closures.</p>
        ) : (
          <div className="space-y-2">
            {draft.blocked_days.map((b) => (
              <div key={b.date} data-testid={`schedule-blocked-day-${b.date}`} className="flex items-center justify-between border border-[#f0ebe0] rounded-lg px-4 py-2.5">
                <div>
                  <span className="font-semibold text-[#0d1f15]">{fmtDate(b.date)}</span>
                  <span className="text-xs text-[#5a7062] ml-2">{b.reason}</span>
                </div>
                <DangerButton testId={`schedule-remove-blocked-day-${b.date}`} onClick={() => setRemoveTarget(b.date)}>
                  <Trash2 size={13} />
                </DangerButton>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={!!removeTarget}
        title="Reopen this day?"
        message="Customers will be able to book sessions on this date again."
        confirmLabel="Remove Closure"
        accent="danger"
        onConfirm={removeBlockedDay}
        onClose={() => setRemoveTarget(null)}
      />
    </div>
  );
};

export default ScheduleSection;
